import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Edit3, FileText, Check, AlertCircle } from 'lucide-react';
import { GeneratedPrompt, PromptStructuredSections } from '../../services/api/imageGenerationService';
import { shouldShowPrompt } from '../../utils/environment';

// 旧 PromptPreviewEditor.scss を内製化した Tailwind 定数。
// 状態が排他になる箇所（mode-toggle の active / char-count の over-limit / btn の
// primary・secondary）は、重複プロパティを持たない完全な文字列に分けてある。
const EDITOR_ROOT = 'bg-white border border-gray-200 rounded-xl overflow-hidden';
const EDITOR_HEADER =
  'flex items-center justify-between px-5 py-4 border-b border-b-gray-200 bg-gray-50 ' +
  'max-[640px]:flex-col max-[640px]:items-start max-[640px]:gap-2';
const EDITOR_TITLE =
  'flex items-center gap-2 text-[15px] font-semibold text-gray-800 [&_svg]:text-blue-500';
const EDITOR_META = 'flex items-center gap-2 text-[13px]';
const PRODUCT_NAME = 'text-blue-500 font-medium';
const STYLE_NAME = 'text-gray-500';
const MODE_TOGGLE =
  'flex gap-2 px-5 py-3 border-b border-b-gray-200 bg-[#fafbfc] max-[640px]:flex-wrap';
const MODE_BTN_BASE =
  'px-4 py-2 rounded-md text-[13px] font-medium cursor-pointer transition-all duration-200 ' +
  'max-[640px]:flex-1';
// SCSS では .active が :hover より後に定義されており同特異度で勝つため、active に hover は無い
const MODE_BTN_IDLE = 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-800';
const MODE_BTN_ACTIVE = 'bg-blue-500 border border-blue-500 text-white';
const EDITOR_CONTENT = 'p-5 max-h-[400px] overflow-y-auto';
const PROMPT_VIEW = 'flex flex-col gap-3';
const PROMPT_SECTION = 'flex flex-col gap-1';
const SECTION_LABEL = 'text-[13px] font-semibold text-blue-500';
const SECTION_CONTENT = 'text-sm text-gray-700 leading-[1.6] whitespace-pre-wrap';
const EDIT_SECTIONS = 'flex flex-col gap-4';
const SECTION_EDIT = 'flex flex-col gap-1.5';
const TEXTAREA_FOCUS =
  'focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]';
const SECTION_INPUT =
  'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 leading-[1.5] ' +
  'resize-y transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 ' +
  TEXTAREA_FOCUS;
const FULLTEXT_INPUT =
  'w-full p-4 border border-gray-300 rounded-lg text-sm text-gray-700 leading-[1.6] resize-y ' +
  'min-h-[300px] transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 ' +
  TEXTAREA_FOCUS;
const CHAR_COUNT_BASE =
  'flex items-center justify-end gap-1.5 px-5 py-2 text-[13px] border-t border-t-gray-200';
const CHAR_COUNT_OK = 'text-gray-500 bg-gray-50';
const CHAR_COUNT_OVER = 'text-red-600 bg-red-50 [&_svg]:text-red-600';
const FEEDBACK_BOX = 'px-5 py-4 bg-blue-50 border-t border-t-blue-200';
const FEEDBACK_LABEL = 'block text-[13px] font-medium text-blue-800 mb-2';
const FEEDBACK_INPUT =
  'w-full px-3 py-2.5 border border-blue-300 rounded-lg text-sm text-gray-700 leading-[1.5] ' +
  'resize-y bg-white transition-[border-color,box-shadow] duration-200 placeholder:text-blue-300 ' +
  'focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]';
const EDITOR_ACTIONS =
  'flex items-center justify-end gap-3 px-5 py-4 border-t border-t-gray-200 bg-gray-50 ' +
  'max-[640px]:flex-wrap';
const BTN_BASE =
  'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-[14px] font-medium ' +
  'cursor-pointer transition-all duration-200 [&_svg]:shrink-0 disabled:opacity-50 ' +
  'disabled:cursor-not-allowed max-[640px]:flex-1 max-[640px]:min-w-[120px]';
const BTN_SECONDARY =
  `${BTN_BASE} bg-white border border-gray-300 text-gray-700 ` +
  'enabled:hover:bg-gray-100 enabled:hover:border-gray-400';
const BTN_PRIMARY =
  `${BTN_BASE} bg-blue-500 border border-blue-500 text-white ` +
  'enabled:hover:bg-blue-600 enabled:hover:border-blue-600';

// Section labels in Japanese
const SECTION_LABELS: Record<keyof PromptStructuredSections, string> = {
  purpose: '目的・用途',
  tone: 'トーン・世界観',
  color_design: '色・デザイン',
  subjects: '被写体・視覚的な要素',
  brand_expression: '製品・ブランドの表現',
  text_copy: 'テキスト・キャッチコピー',
  layout: 'フォント・レイアウト',
};

// Order of sections for display
const SECTION_ORDER: (keyof PromptStructuredSections)[] = [
  'purpose',
  'tone',
  'color_design',
  'subjects',
  'brand_expression',
  'text_copy',
  'layout',
];

// Fallback when the API response omits structured_sections (optional field).
const EMPTY_STRUCTURED_SECTIONS: PromptStructuredSections = {
  purpose: '',
  tone: '',
  color_design: '',
  subjects: '',
  brand_expression: '',
  text_copy: '',
  layout: '',
};

interface PromptPreviewEditorProps {
  generatedPrompt: GeneratedPrompt;
  onRegenerate: (feedback?: string) => void;
  onConfirm: (finalPrompt: string) => void;
  onEdit?: (editedPrompt: string) => void;
  isRegenerating?: boolean;
  maxCharCount?: number;
}

type EditMode = 'view' | 'sections' | 'fulltext';

const PromptPreviewEditor: React.FC<PromptPreviewEditorProps> = ({
  generatedPrompt,
  onRegenerate,
  onConfirm,
  onEdit,
  isRegenerating = false,
  maxCharCount = 600,
}) => {
  const [editMode, setEditMode] = useState<EditMode>('view');
  const [editedSections, setEditedSections] = useState<PromptStructuredSections>(
    generatedPrompt.structured_sections ?? EMPTY_STRUCTURED_SECTIONS
  );
  const [editedFullText, setEditedFullText] = useState<string>(generatedPrompt.prompt);
  const [regenerateFeedback, setRegenerateFeedback] = useState<string>('');
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);

  // Update local state when generatedPrompt changes
  useEffect(() => {
    setEditedSections(generatedPrompt.structured_sections ?? EMPTY_STRUCTURED_SECTIONS);
    setEditedFullText(generatedPrompt.prompt);
    setEditMode('view');
    setRegenerateFeedback('');
    setShowFeedbackInput(false);
  }, [generatedPrompt]);

  // Build prompt from sections
  const buildPromptFromSections = useCallback((sections: PromptStructuredSections): string => {
    return SECTION_ORDER
      .map((key) => {
        const label = SECTION_LABELS[key];
        const content = sections[key];
        return content ? `【${label}】${content}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }, []);

  // Get current character count
  const getCurrentCharCount = useCallback((): number => {
    if (editMode === 'fulltext') {
      return editedFullText.length;
    } else if (editMode === 'sections') {
      return buildPromptFromSections(editedSections).length;
    }
    return generatedPrompt.prompt.length;
  }, [editMode, editedFullText, editedSections, generatedPrompt.prompt, buildPromptFromSections]);

  // Get current prompt text
  const getCurrentPrompt = useCallback((): string => {
    if (editMode === 'fulltext') {
      return editedFullText;
    } else if (editMode === 'sections') {
      return buildPromptFromSections(editedSections);
    }
    return generatedPrompt.prompt;
  }, [editMode, editedFullText, editedSections, generatedPrompt.prompt, buildPromptFromSections]);

  // Handle section edit
  const handleSectionChange = useCallback(
    (key: keyof PromptStructuredSections, value: string) => {
      setEditedSections((prev) => ({
        ...prev,
        [key]: value,
      }));
      if (onEdit) {
        const newSections = { ...editedSections, [key]: value };
        onEdit(buildPromptFromSections(newSections));
      }
    },
    [editedSections, onEdit, buildPromptFromSections]
  );

  // Handle full text edit
  const handleFullTextChange = useCallback(
    (value: string) => {
      setEditedFullText(value);
      if (onEdit) {
        onEdit(value);
      }
    },
    [onEdit]
  );

  // Handle regenerate
  const handleRegenerate = useCallback(() => {
    if (showFeedbackInput && regenerateFeedback.trim()) {
      onRegenerate(regenerateFeedback.trim());
      setShowFeedbackInput(false);
      setRegenerateFeedback('');
    } else if (!showFeedbackInput) {
      setShowFeedbackInput(true);
    } else {
      // No feedback, just regenerate
      onRegenerate();
      setShowFeedbackInput(false);
    }
  }, [onRegenerate, showFeedbackInput, regenerateFeedback]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    onConfirm(getCurrentPrompt());
  }, [onConfirm, getCurrentPrompt]);

  // Start editing in specified mode
  const startEditing = useCallback((mode: EditMode) => {
    if (mode === 'fulltext') {
      // Sync from sections if we were in section mode
      setEditedFullText(buildPromptFromSections(editedSections));
    }
    setEditMode(mode);
  }, [editedSections, buildPromptFromSections]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditedSections(generatedPrompt.structured_sections ?? EMPTY_STRUCTURED_SECTIONS);
    setEditedFullText(generatedPrompt.prompt);
    setEditMode('view');
  }, [generatedPrompt]);

  const charCount = getCurrentCharCount();
  const isOverLimit = charCount > maxCharCount;
  const showPromptContent = shouldShowPrompt();

  // Production environment: simplified UI without prompt visibility
  if (!showPromptContent) {
    return (
      <div className={EDITOR_ROOT}>
        {/* Header */}
        <div className={EDITOR_HEADER}>
          <div className={EDITOR_TITLE}>
            <FileText size={18} />
            <span>画像生成の準備完了</span>
          </div>
          <div className={EDITOR_META}>
            <span className={PRODUCT_NAME}>
              {generatedPrompt.product_name}
            </span>
            {generatedPrompt.style_name && (
              <span className={STYLE_NAME}>
                / {generatedPrompt.style_name}
              </span>
            )}
          </div>
        </div>

        {/* Simple message for production */}
        <div className={EDITOR_CONTENT}>
          <div className={PROMPT_VIEW}>
            <p className="text-sm text-gray-600 py-4 text-center">
              プロンプトが生成されました。
            </p>
          </div>
        </div>

        {/* Actions - Only generate button in production */}
        <div className={EDITOR_ACTIONS}>
          <button
            className={BTN_PRIMARY}
            onClick={handleConfirm}
            disabled={isRegenerating}
          >
            <Check size={16} />
            <span>画像を生成</span>
          </button>
        </div>
      </div>
    );
  }

  // Staging/Local environment: full UI with prompt visibility
  return (
    <div className={EDITOR_ROOT}>
      {/* Header */}
      <div className={EDITOR_HEADER}>
        <div className={EDITOR_TITLE}>
          <FileText size={18} />
          <span>生成されたプロンプト</span>
        </div>
        <div className={EDITOR_META}>
          <span className={PRODUCT_NAME}>
            {generatedPrompt.product_name}
          </span>
          {generatedPrompt.style_name && (
            <span className={STYLE_NAME}>
              / {generatedPrompt.style_name}
            </span>
          )}
        </div>
      </div>

      {/* Edit Mode Toggle (only visible in edit mode) */}
      {editMode !== 'view' && (
        <div className={MODE_TOGGLE}>
          <button
            className={`${MODE_BTN_BASE} ${editMode === 'sections' ? MODE_BTN_ACTIVE : MODE_BTN_IDLE}`}
            onClick={() => startEditing('sections')}
          >
            セクション別
          </button>
          <button
            className={`${MODE_BTN_BASE} ${editMode === 'fulltext' ? MODE_BTN_ACTIVE : MODE_BTN_IDLE}`}
            onClick={() => startEditing('fulltext')}
          >
            全体編集
          </button>
        </div>
      )}

      {/* Prompt Content */}
      <div className={EDITOR_CONTENT}>
        {editMode === 'view' && (
          <div className={PROMPT_VIEW}>
            {SECTION_ORDER.map((key) => {
              const content = generatedPrompt.structured_sections[key];
              if (!content) return null;
              return (
                <div key={key} className={PROMPT_SECTION}>
                  <div className={SECTION_LABEL}>【{SECTION_LABELS[key]}】</div>
                  <div className={SECTION_CONTENT}>{content}</div>
                </div>
              );
            })}
          </div>
        )}

        {editMode === 'sections' && (
          <div className={EDIT_SECTIONS}>
            {SECTION_ORDER.map((key) => (
              <div key={key} className={SECTION_EDIT}>
                <label className={SECTION_LABEL}>
                  【{SECTION_LABELS[key]}】
                </label>
                <textarea
                  className={SECTION_INPUT}
                  value={editedSections[key]}
                  onChange={(e) => handleSectionChange(key, e.target.value)}
                  rows={2}
                  placeholder={`${SECTION_LABELS[key]}を入力...`}
                />
              </div>
            ))}
          </div>
        )}

        {editMode === 'fulltext' && (
          <div className="w-full">
            <textarea
              className={FULLTEXT_INPUT}
              value={editedFullText}
              onChange={(e) => handleFullTextChange(e.target.value)}
              rows={15}
              placeholder="プロンプトを入力..."
            />
          </div>
        )}
      </div>

      {/* Character Count */}
      <div className={`${CHAR_COUNT_BASE} ${isOverLimit ? CHAR_COUNT_OVER : CHAR_COUNT_OK}`}>
        {isOverLimit && <AlertCircle size={14} />}
        <span>
          文字数: {charCount}文字
          {maxCharCount && ` / ${maxCharCount}文字`}
        </span>
      </div>

      {/* Regenerate Feedback Input */}
      {showFeedbackInput && (
        <div className={FEEDBACK_BOX}>
          <label className={FEEDBACK_LABEL}>改善のためのフィードバック（任意）</label>
          <textarea
            className={FEEDBACK_INPUT}
            value={regenerateFeedback}
            onChange={(e) => setRegenerateFeedback(e.target.value)}
            rows={2}
            placeholder="例: もっと明るい雰囲気にしてください"
          />
        </div>
      )}

      {/* Actions */}
      <div className={EDITOR_ACTIONS}>
        {editMode === 'view' ? (
          <>
            <button
              className={BTN_SECONDARY}
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              <RefreshCw size={16} className={isRegenerating ? 'animate-spin' : ''} />
              <span>{showFeedbackInput ? 'フィードバックを送信' : '再生成'}</span>
            </button>
            <button
              className={BTN_SECONDARY}
              onClick={() => startEditing('sections')}
            >
              <Edit3 size={16} />
              <span>編集する</span>
            </button>
            <button
              className={BTN_PRIMARY}
              onClick={handleConfirm}
              disabled={isRegenerating}
            >
              <Check size={16} />
              <span>このまま画像を生成</span>
            </button>
          </>
        ) : (
          <>
            <button className={BTN_SECONDARY} onClick={cancelEditing}>
              キャンセル
            </button>
            <button
              className={BTN_PRIMARY}
              onClick={handleConfirm}
              disabled={isOverLimit}
            >
              <Check size={16} />
              <span>画像を生成</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PromptPreviewEditor;
